/* ---------------------------------------------------------------------------
   Philippines, Department of Public Works and Highways.

   The second department this application serves, and the one that proved the
   tenant contract was the right shape — three things it needs that DoR does not,
   each of which had a place to go without a new switch anywhere:

     1. **Stations, not chainages.** DPWH marks a position as `796+180`. Same
        `Bridge.km`, different rendering — `chainage: 'station'`.
     2. **Contract packages.** The register's first-order division is not the
        district; it is the bid package a structure falls in (S1–S4, A1–A5,
        B1–B3). That is not a `Bridge` column and should not become one, so it is
        an extra field and the leading grouping.
     3. **A register with almost nothing in it.** Six facts per structure, and
        the rest genuinely unrecorded. See src/data/dpwh.ts.

   WHAT THIS PROFILE DELIBERATELY LEAVES EMPTY, because empty is the true answer
   and a plausible-looking value would be worse:

     · `roadClasses` is `[]`. DPWH classifies its national roads (primary,
       secondary, tertiary) and numbers its route sections, but THIS REGISTER
       CARRIES NEITHER — the source lists highway names, not route codes, so
       every record's `route` is null. Rules matching codes that are never
       present would classify nothing while looking as though they classified
       something, and the `class` grouping is therefore absent from the list
       below rather than present and always empty. Both come back the day route
       sections are imported.
     · `geo.outline` is null. See the header of src/data/philippines.ts, which
       is the whole argument and is worth reading before anybody adds a coastline.
     · `locales` is English only. DPWH works in English; Filipino is not
       half-translated here on the strength of a guess. There is no language
       control on this tenant at all, which is the point — see `locales` in
       src/tenant/types.ts.
   --------------------------------------------------------------------------- */

import { DMRP_CORRIDOR, PHILIPPINES_BOUNDS } from '../../data/philippines.ts';
import type { ExtraField, TenantProfile } from '../types.ts';

/**
 * The two packages whose condition survey is a contractual deliverable.
 *
 * From the DMRP market-sounding pack of March 2026: S1 and B1 are advertised
 * first, and their survey falls in month 3–4 of construction. Exported because
 * the register highlights them and the dashboard counts them, and a list of
 * priorities that existed twice would eventually be two lists.
 */
export const DPWH_PRIORITY_PACKAGES: readonly string[] = ['S1', 'B1'];

/** True where a package code is one of the two advertised first. */
export function isPriorityPackage(code: string | null | undefined): boolean {
  return code != null && DPWH_PRIORITY_PACKAGES.includes(code);
}

/**
 * The five columns DPWH's list has and DoR's sheet does not.
 *
 * `station` is stored as TEXT even though `Bridge.km` holds the same position as
 * a number, and that redundancy is deliberate. DPWH stations are not always
 * normalised: the source carries `848+1066` — 1066 m past kilometre 848 — and
 * `849+066` is the same point written the way an office would normally write it.
 * Rendering the number back would silently correct somebody's drawing. So the
 * number is what the map and the sort use, the string is what the screen shows
 * beside a printed plan, and where they disagree the register shows the string
 * it was given.
 */
const EXTRAS: readonly ExtraField[] = [
  {
    key: 'package',
    label: 'Contract package',
    kind: 'code',
    note: 'Bid package the structure falls in. S1 and B1 are advertised first.',
  },
  {
    key: 'section',
    label: 'Highway section',
    kind: 'text',
  },
  {
    key: 'station',
    label: 'Station',
    kind: 'code',
    note: 'As written in the source list, which is not always normalised — see the profile header.',
  },
  {
    key: 'lanes',
    label: 'Traffic lanes',
    kind: 'number',
    note: 'Lane count, not a width. Carriageway width is not recorded for these structures.',
  },
  {
    key: 'barangay',
    label: 'Barangay',
    kind: 'text',
    note: 'From DPWH’s Road & Bridge Inventory, where it has a record — see src/data/dpwh-rbi.ts.',
  },
  {
    key: 'municipality',
    label: 'City / municipality',
    kind: 'text',
    note: 'From DPWH’s Road & Bridge Inventory, where it has a record.',
  },
  {
    key: 'assessedCondition',
    label: 'Assessed condition',
    kind: 'text',
    note:
      'DESK ASSESSMENT from the DMRP market-sounding pack, March 2026. Not an inspection, ' +
      'not a survey, and not derived from any element rating — no structure in this ' +
      'register has been inspected. Never quote it as a condition rating.',
  },
];

export const PH_DPWH: TenantProfile = {
  id: 'ph-dpwh',
  name: 'Philippines — DPWH',
  country: 'PH',
  countryName: 'Philippines',

  agency: {
    government: 'Republic of the Philippines',
    ministry: 'Department of Public Works and Highways',
    department: 'Department of Public Works and Highways',
    departmentShort: 'DPWH',
    branch: 'Bureau of Design',
    unit: 'Daang Maharlika Road Improvement Project (ADB-funded)',
    unitShort: 'Daang Maharlika Road Improvement Project',
    seal: 'PH',
  },

  brand: {
    emblems: [
      { src: 'brand/ph-dpwh/coat-of-arms.webp', alt: 'Coat of arms of the Philippines' },
      { src: 'brand/ph-dpwh/dpwh-seal.png', alt: 'DPWH seal', disc: true },
    ],
    logo: 'brand/ph-dpwh/dpwh-seal.png',
    /* The seal's own blue, and the flag's blue, red and gold. */
    ground: '#14288f',
    stripe: ['#0038a8', '#ce1126', '#fcd116'],
  },

  /* National bounds, project-corridor home view. DPWH is a national department,
     so a coordinate anywhere in the archipelago is a valid record rather than an
     error — but a register of 106 structures on three sections should not open
     zoomed out across two thousand kilometres of ocean. */
  geo: {
    bounds: PHILIPPINES_BOUNDS,
    home: DMRP_CORRIDOR,
    outline: null,
    outlineProvenance: null,
  },

  /* DPWH's own words. `area` is District — meaning the District Engineering
     Office's district, which is what the source list divides by — and `office`
     spells that out in full where there is room for it. */
  vocab: {
    area: 'District',
    areaPlural: 'Districts',
    /* PROVINCE, NOT REGION. The wider area this register can actually fill is
       the province in a District Engineering Office's name — Samar, Agusan del
       Sur, Camarines Sur, Quezon. Labelling that column "Region" would put a
       province under a heading for the administrative level above it. */
    region: 'Province',
    office: 'District Engineering Office',
    locality: 'Barangay',
    road: 'Highway',
    chainage: 'Station',
  },

  chainage: 'station',

  /* Empty, and the header says why at length. Do not add rules for codes this
     register does not carry. */
  roadClasses: [],

  extras: EXTRAS,

  /* Package first, because that is how the work is let and how everybody in the
     project refers to a structure. No `class` grouping — there are no route
     codes to classify. */
  groupings: [
    { key: 'package', label: 'Contract package', of: (b) => text(b.tenantFields.package) },
    { key: 'section', label: 'Highway section', of: (b) => text(b.tenantFields.section) },
    { key: 'district', label: 'District Engineering Office', of: (b) => b.district || null },
  ],

  currency: {
    code: 'PHP',
    symbol: '₱',
    unit: 'peso',
    fiscalPeriodLabel: 'Calendar year (e.g. 2026)',
  },

  locales: ['en'],

  /* NULL, DELIBERATELY. The Philippines is seismically active and the NSCP sets
     seismic zoning, but nobody on this project has established what that
     document says closely enough to print a sentence about it over a
     government's own bridge register. The general caveat — the zoning factor is
     jurisdiction data the register does not hold, so what is ranked is
     vulnerability — is true without it, and is what the screen shows. Fill this
     in from the code itself or leave it null; do not fill it in from the fact
     that the country has earthquakes. */
  seismicZoningNote: null,

  registerProvenanceShort: 'A market-sounding listing, plus DPWH inventory for 43 of 108',

  registerProvenance:
    '108 structures from the DPWH Daang Maharlika Road Improvement Project market-sounding ' +
    'pack, March 2026 — which lists 108 while its own heading says 106. That pack gives the ' +
    'name, bridge ID, package, station, length and lane count, and nothing else; no ' +
    'structure in it has been inspected, and the condition shown against each is a desk ' +
    'assessment from it rather than a survey. 43 of the 108 also carry a position, width, ' +
    'year, superstructure and province matched from DPWH’s own Road & Bridge Inventory ' +
    'through OpenStreetMap — marked R on screen. Spans, deck levels, design load and ' +
    'foundations are not recorded for any of the 108.',

  /* PROVISIONAL (LC-24). CBPLR 2009 states no re-rating interval; five years,
     and two for a posted bridge, until DPWH says otherwise. */
  rerating: { years: 5, restrictedYears: 2 },

  /* PROVISIONAL (LC-61). Annual routine and five-yearly principal, as the
     cycle always ran; six months and three years for a restricted bridge or
     one whose latest condition is 3 of 4 or worse. */
  inspection: { routineMonths: 12, principalMonths: 60, shortRoutineMonths: 6, shortPrincipalMonths: 36, poorFrom: 0.75 },
};

/**
 * One extra field as a group key, or null.
 *
 * A grouping key has to be a non-empty string or absent, and an extra field is
 * `string | number | null` by contract — so a package stored as `0` or `''` is
 * *not recorded* here rather than a group called "0". Local to this file because
 * it exists for these three groupings and nothing else.
 */
function text(value: string | number | null | undefined): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s === '' ? null : s;
}
